import { PageCanvas, FormContent } from "@/components/page-canvas";
import CaseWorkspace, {
  CaseFacts,
  CaseNavigation,
} from "@/components/case-workspace";
import { Form, Head, Link, usePage } from "@inertiajs/react";
import { useTranslation } from "react-i18next";
import AppLayout from "@/layouts/app_layout";
import { Button, buttonVariants } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  newLocationRecordingOperationalNotificationPath,
  locationRecordingOperationalNotificationsPath,
  locationRequestPath,
  locationJobPath,
  editLocationRequestProposalDraftPath,
  locationRequestProposalDraftPath,
  locationRequestProposalPublicationPath,
} from "@/routes";
import { Errors } from "../requests/controls";
import ProposalContent from "./content";
import type { Representation } from "./types";

type Published = {
  revision: string;
  representation: Representation;
  published_at: string;
  decision_state: string;
};
type Delivery = {
  state:
    "unpublished" | "not_requested" | "queued" | "sending" | "sent" | "failed";
  email_available: boolean;
  latest_attempt: {
    id: string;
    recipient: string | null;
    state: string;
    failure_code: string | null;
    sent_at: string | null;
  } | null;
};
export default function Preview({
  enquiry,
  proposal,
  draft_revision,
  sent_id,
  sent_revision,
  publishable,
  viewing_url,
  history,
  hold_hours,
  section,
  delivery,
  published,
}: {
  enquiry: {
    id: string;
    revision: string;
    name: string;
    vehicle: string;
    state: string;
    job_id: string | null;
  };
  proposal: { representation: Representation | null };
  draft_revision: string;
  sent_id: string | null;
  sent_revision: string | null;
  publishable: boolean;
  hold_hours: number;
  viewing_url: string | null;
  history: {
    id: string;
    representation: Representation;
    published_at: string;
    actor: string | null;
  }[];
  section: "review" | "delivery";
  delivery: Delivery;
  published: Published | null;
}) {
  const { auth } = usePage().props;
  const { t } = useTranslation();
  if (!auth.location) return null;
  const location = auth.location.id;
  const requestPath = locationRequestPath(location, enquiry.id);
  const represented =
    section === "delivery"
      ? published?.representation
      : proposal.representation;
  const notificationPath =
    sent_id && locationRecordingOperationalNotificationsPath(location, sent_id);
  const editable = !["accepted", "declined"].includes(enquiry.state);
  const terminal =
    published && ["accepted", "declined"].includes(published.decision_state);
  const date = (value: string, offer: Representation) =>
    new Intl.DateTimeFormat(offer.locale, {
      dateStyle: "medium",
      timeStyle: "short",
      timeZone: offer.schedule.time_zone,
    }).format(new Date(value));
  return (
    <AppLayout
      showWorkspaceNavigation={false}
      breadcrumbs={[{ title: t("requests.title"), href: requestPath }]}
    >
      <Head
        title={t(
          section === "review"
            ? "proposals.preview"
            : "product_ux_proposal.delivery",
        )}
      />
      <PageCanvas>
        <CaseWorkspace
          fluid
          editor={false}
          context={
            <CaseFacts
              facts={[
                {
                  label: t("product_ux_proposal.customer"),
                  value: represented?.customer.name || enquiry.name,
                },
                {
                  label: t("product_ux_proposal.vehicle"),
                  value: represented
                    ? `${represented.vehicle.make} ${represented.vehicle.model}`
                    : enquiry.vehicle,
                },
                {
                  label: t("product_ux_proposal.status"),
                  value: t(
                    section === "review"
                      ? "product_ux_proposal.private_draft"
                      : `product_ux_proposal.states.${delivery.state}`,
                  ),
                },
              ]}
            />
          }
          navigation={
            <CaseNavigation
              label={t("product_ux_proposal.sections")}
              active={section}
              items={[
                {
                  id: "services",
                  disabled: !editable,
                  label: t("product_ux_proposal.services"),
                  href: editLocationRequestProposalDraftPath(
                    location,
                    enquiry.id,
                    { section: "services" },
                  ),
                },
                {
                  id: "schedule",
                  disabled: !editable,
                  label: t("product_ux_proposal.schedule"),
                  href: editLocationRequestProposalDraftPath(
                    location,
                    enquiry.id,
                    { section: "schedule" },
                  ),
                },
                {
                  id: "review",
                  label: t("product_ux_proposal.review"),
                  href: locationRequestProposalDraftPath(location, enquiry.id, {
                    section: "review",
                  }),
                },
                {
                  id: "delivery",
                  label: t("product_ux_proposal.delivery"),
                  href: locationRequestProposalDraftPath(location, enquiry.id, {
                    section: "delivery",
                  }),
                  disabled: !published,
                },
              ]}
            />
          }
        >
          <div className="space-y-6">
            <FormContent>
              <h1 className="text-page font-display">
                {t(
                  section === "review"
                    ? "product_ux_proposal.review"
                    : "product_ux_proposal.delivery_title",
                )}
              </h1>
              <p className="text-muted-foreground text-sm">
                {t(
                  section === "review"
                    ? "product_ux_proposal.review_help"
                    : "product_ux_proposal.delivery_help",
                )}
              </p>
              <Errors />
            </FormContent>
            {section === "review" ? (
              <>
                {publishable && (
                  <FormContent>
                    <p className="text-muted-foreground text-sm">
                      {t("proposals.publish_hold", { hours: hold_hours })}
                    </p>
                  </FormContent>
                )}
                {proposal.representation ? (
                  <ProposalContent
                    representation={proposal.representation}
                    headingTag="h2"
                  />
                ) : (
                  <p role="alert">{t("proposals.recompose")}</p>
                )}
                <FormContent>
                  <div className="flex flex-wrap gap-3">
                    {editable && (
                      <Link
                        className={buttonVariants({ variant: "outline" })}
                        href={editLocationRequestProposalDraftPath(
                          location,
                          enquiry.id,
                          { section: "services" },
                        )}
                      >
                        {t("proposals.edit")}
                      </Link>
                    )}
                    {publishable && (
                      <Form
                        action={locationRequestProposalPublicationPath(
                          location,
                          enquiry.id,
                        )}
                        method="post"
                      >
                        {({ processing }) => (
                          <>
                            <input
                              type="hidden"
                              name="request_revision"
                              value={enquiry.revision}
                            />
                            <input
                              type="hidden"
                              name="draft_revision"
                              value={draft_revision}
                            />
                            <input
                              type="hidden"
                              name="sent_revision"
                              value={sent_revision || ""}
                            />
                            <Button type="submit" disabled={processing}>
                              {t("proposals.publish")}
                            </Button>
                          </>
                        )}
                      </Form>
                    )}
                    <Link
                      className={buttonVariants({ variant: "ghost" })}
                      href={requestPath}
                    >
                      {t("common.cancel")}
                    </Link>
                  </div>
                  {published && (
                    <Link
                      className="inline-block underline"
                      href={locationRequestProposalDraftPath(
                        location,
                        enquiry.id,
                        { section: "delivery" },
                      )}
                    >
                      {t("product_ux_proposal.delivery")}
                    </Link>
                  )}
                </FormContent>
              </>
            ) : published ? (
              <>
                <div className="space-y-6">
                  {publishable && (
                    <p role="status" className="rounded-lg border p-4">
                      {t("product_ux_proposal.private_changes")}
                    </p>
                  )}
                  {terminal ? (
                    <section
                      data-layout-frame="proposal-outcome"
                      aria-label={t("product_ux_proposal.outcome")}
                      className="rounded-lg border bg-card p-4"
                    >
                      <FormContent className="space-y-3">
                        <h2 className="font-semibold">
                          {t("product_ux_proposal.outcome")}
                        </h2>
                        <p role="status">
                          {t(`proposals.${published.decision_state}_status`)}
                        </p>
                        {enquiry.job_id && (
                          <Link
                            className={buttonVariants()}
                            href={locationJobPath(location, enquiry.job_id)}
                          >
                            {t("product_ux_proposal.open_job")}
                          </Link>
                        )}
                      </FormContent>
                    </section>
                  ) : (
                    <section
                      className="rounded-lg border bg-card p-4"
                      data-layout-frame="proposal-delivery-attempt"
                      aria-label={t("product_ux_proposal.current_attempt")}
                    >
                      <FormContent className="space-y-4">
                        <h2 className="font-semibold">
                          {t("product_ux_proposal.current_attempt")}
                        </h2>
                        <p role="status">
                          {t(`product_ux_proposal.states.${delivery.state}`)}
                        </p>
                        {delivery.latest_attempt?.recipient && (
                          <p className="break-words">
                            {t("notifications.recipient")}:{" "}
                            {delivery.latest_attempt.recipient}
                          </p>
                        )}
                        {delivery.latest_attempt?.sent_at && (
                          <p className="text-sm text-muted-foreground">
                            {date(
                              delivery.latest_attempt.sent_at,
                              published.representation,
                            )}
                          </p>
                        )}
                        {delivery.latest_attempt?.failure_code && (
                          <p role="alert">
                            {t(
                              `notifications.${delivery.latest_attempt.failure_code}`,
                            )}
                          </p>
                        )}
                        <p className="text-muted-foreground text-sm">
                          {t(
                            delivery.email_available
                              ? "product_ux_proposal.email_available"
                              : "product_ux_proposal.email_unavailable",
                          )}
                        </p>
                        <div className="flex flex-wrap gap-3">
                          {delivery.email_available && sent_id && (
                            <Link
                              className={buttonVariants()}
                              href={newLocationRecordingOperationalNotificationPath(
                                location,
                                sent_id,
                              )}
                            >
                              {t("product_ux_proposal.preview_message")}
                            </Link>
                          )}
                          {notificationPath && (
                            <Link
                              className={buttonVariants({ variant: "outline" })}
                              href={notificationPath}
                            >
                              {t("product_ux_proposal.history")}
                            </Link>
                          )}
                        </div>
                      </FormContent>
                    </section>
                  )}
                  {viewing_url && (
                    <section
                      className="rounded-lg border bg-card p-4"
                      data-layout-frame="proposal-manual-handoff"
                      aria-label={t("product_ux_proposal.manual_title")}
                    >
                      <FormContent className="space-y-3">
                        <h2 className="font-semibold">
                          {t("product_ux_proposal.manual_title")}
                        </h2>
                        <Label htmlFor="proposal-link">
                          {t("proposals.link")}
                        </Label>
                        <Input
                          id="proposal-link"
                          readOnly
                          value={viewing_url}
                          onFocus={(event) => event.target.select()}
                        />
                        <p className="text-muted-foreground text-sm">
                          {t("product_ux_proposal.manual_help")}
                        </p>
                        <div className="flex flex-wrap gap-3">
                          <a className="underline" href={viewing_url}>
                            {t("proposals.open")}
                          </a>
                          <Link
                            className="underline"
                            href={locationRequestPath(location, enquiry.id, {
                              section: "conversation",
                            })}
                          >
                            {t("workflow.conversation")}
                          </Link>
                        </div>
                      </FormContent>
                    </section>
                  )}
                </div>
                <details
                  data-layout-frame="proposal-published-document"
                  className="rounded-lg border bg-card p-4"
                >
                  <summary className="cursor-pointer font-semibold">
                    {t("product_ux_proposal.published_title")} ·{" "}
                    {date(published.published_at, published.representation)}
                  </summary>
                  <p className="mt-3 text-muted-foreground text-sm">
                    {t("product_ux_proposal.published_help")}
                  </p>
                  <div className="mt-6">
                    <ProposalContent
                      representation={published.representation}
                      headingTag="h3"
                    />
                  </div>
                </details>
              </>
            ) : (
              <FormContent>
                <p role="status">{t("product_ux_proposal.no_publication")}</p>
                <Link
                  className="underline"
                  href={locationRequestProposalDraftPath(location, enquiry.id, {
                    section: "review",
                  })}
                >
                  {t("product_ux_proposal.review")}
                </Link>
              </FormContent>
            )}
            {history.length > 0 && (
              <section className="space-y-3">
                <h2 className="text-section font-semibold">
                  {t("proposals.history")}
                </h2>
                {history.map((version) => (
                  <details
                    data-layout-frame=""
                    key={version.id}
                    className="rounded-lg border bg-card p-4"
                  >
                    <summary className="cursor-pointer">
                      {version.actor} ·{" "}
                      {date(version.published_at, version.representation)}
                    </summary>
                    <div className="mt-6">
                      <ProposalContent
                        representation={version.representation}
                        headingTag="h3"
                      />
                    </div>
                  </details>
                ))}
              </section>
            )}
          </div>
        </CaseWorkspace>
      </PageCanvas>
    </AppLayout>
  );
}
